"use client";

import React from "react";
import Image from "next/image";
import { useState, useEffect, useRef, useCallback } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useTranslation } from "@/hooks/useTranslation";
import { useTheme } from "next-themes";
import { useRTL } from "@/hooks/useRTL";
import { motion, AnimatePresence } from "framer-motion";
import { useRouter } from "next/navigation";
import { supportedLanguages, languageNames } from "@/lib/i18n";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { Sun, Moon, Menu, X, Stethoscope, ChevronDown, ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";

// ─── Trustpilot constants ────────────────────────────────────────────────────
const TRUSTPILOT_URL = "https://www.trustpilot.com/review/dentawista.com";
const TRUSTPILOT_RATING = 4.0;

function TrustpilotMiniStars({ rating }: { rating: number }) {
  const filled = Math.round(rating);
  return (
    <div className="flex gap-[2px]">
      {[1, 2, 3, 4, 5].map((star) => (
        <svg key={star} width="14" height="14" viewBox="0 0 96 96" fill="none">
          <rect
            width="96"
            height="96"
            fill={star <= filled ? "#00b67a" : "#dcdce6"}
          />
          <path
            d="M48 64.7L62.6 61L68.7 79.8L48 64.7ZM81.6 40.4H55.9L48 16.2L40.1 40.4H14.4L35.2 55.4L27.3 79.6L48.1 64.6L60.9 55.4L81.6 40.4Z"
            fill="white"
          />
        </svg>
      ))}
    </div>
  );
}

function TrustpilotMiniLogo({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 1132.8 278.2"
      className={className}
      aria-label="Trustpilot"
    >
      <path
        fill="#00b67a"
        d="M271.3 98.6H167.7L135.7 0l-32.1 98.6L0 98.5l83.9 61L51.8 258l83.9-60.9 83.8 60.9-32-98.5 83.8-60.9z"
      />
      <path fill="#005128" d="M194.7 181.8l-7.2-22.3-51.8 37.6z" />
      <path
        className="fill-[#191919] dark:fill-white"
        d="M297.7 98.6h114.7V120h-45.1v120.3h-24.8V120h-44.9V98.6zm109.8 39.1h21.2v19.8h.4c.7-2.8 2-5.5 3.9-8.1 1.9-2.6 4.2-5.1 6.9-7.2 2.7-2.2 5.7-3.9 9-5.3 3.3-1.3 6.7-2 10.1-2 2.6 0 4.5.1 5.5.2s2 .3 3.1.4v21.8c-1.6-.3-3.2-.5-4.9-.7-1.7-.2-3.3-.3-4.9-.3-3.8 0-7.4.8-10.8 2.3-3.4 1.5-6.3 3.8-8.8 6.7-2.5 3-4.5 6.6-6 11s-2.2 9.4-2.2 15.1v48.8h-22.6V137.7zm164 102.6h-22.2V226h-.4c-2.8 5.2-6.9 9.3-12.4 12.4-5.5 3.1-11.1 4.7-16.8 4.7-13.5 0-23.3-3.3-29.3-10s-9-16.8-9-30.3v-65.1H504v62.9c0 9 1.7 15.4 5.2 19.1 3.4 3.7 8.3 5.6 14.5 5.6 4.8 0 8.7-.7 11.9-2.2 3.2-1.5 5.8-3.4 7.7-5.9 2-2.4 3.4-5.4 4.3-8.8.9-3.4 1.3-7.1 1.3-11.1v-59.5h22.6v102.5zm38.5-32.9c.7 6.6 3.2 11.2 7.5 13.9 4.4 2.6 9.6 4 15.7 4 2.1 0 4.5-.2 7.2-.5s5.3-1 7.6-1.9c2.4-.9 4.3-2.3 5.9-4.1 1.5-1.8 2.2-4.1 2.1-7-.1-2.9-1.2-5.3-3.2-7.1-2-1.9-4.5-3.3-7.6-4.5-3.1-1.1-6.6-2.1-10.6-2.9-4-.8-8-1.7-12.1-2.6-4.2-.9-8.3-2.1-12.2-3.4-3.9-1.3-7.4-3.1-10.5-5.4-3.1-2.2-5.6-5.1-7.4-8.6-1.9-3.5-2.8-7.8-2.8-13 0-5.6 1.4-10.2 4.1-14 2.7-3.8 6.2-6.8 10.3-9.1 4.2-2.3 8.8-3.9 13.9-4.9 5.1-.9 10-1.4 14.6-1.4 5.3 0 10.4.6 15.2 1.7 4.8 1.1 9.2 2.9 13.1 5.5 3.9 2.5 7.1 5.8 9.7 9.8 2.6 4 4.2 8.9 4.9 14.6h-23.6c-1.1-5.4-3.5-9.1-7.4-10.9-3.9-1.9-8.4-2.8-13.4-2.8-1.6 0-3.5.1-5.7.4-2.2.3-4.2.8-6.2 1.5-1.9.7-3.5 1.8-4.9 3.2-1.3 1.4-2 3.2-2 5.5 0 2.8 1 5 2.9 6.7 1.9 1.7 4.4 3.1 7.5 4.3 3.1 1.1 6.6 2.1 10.6 2.9 4 .8 8.1 1.7 12.3 2.6 4.1.9 8.1 2.1 12.1 3.4 4 1.3 7.5 3.1 10.6 5.4 3.1 2.3 5.6 5.1 7.5 8.5 1.9 3.4 2.9 7.7 2.9 12.7 0 6.1-1.4 11.2-4.2 15.5-2.8 4.2-6.4 7.7-10.8 10.3-4.4 2.6-9.4 4.6-14.8 5.8-5.4 1.2-10.8 1.8-16.1 1.8-6.5 0-12.5-.7-18-2.2-5.5-1.5-10.3-3.7-14.3-6.6-4-3-7.2-6.7-9.5-11.1-2.3-4.4-3.5-9.7-3.7-15.8H610zm74.6-69.7h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1zm76.1 0h21.4v13.9h.4c3.2-6 7.6-10.2 13.3-12.8 5.7-2.6 11.8-3.9 18.5-3.9 8.1 0 15.1 1.4 21.1 4.3 6 2.8 11 6.7 15 11.7 4 5 6.9 10.8 8.9 17.4 2 6.6 3 13.7 3 21.2 0 6.9-.9 13.6-2.7 20-1.8 6.5-4.5 12.2-8.1 17.2-3.6 5-8.2 8.9-13.8 11.9-5.6 3-12.1 4.5-19.7 4.5-3.3 0-6.6-.3-9.9-.9-3.3-.6-6.5-1.6-9.5-2.9-3-1.3-5.9-3-8.4-5.1-2.6-2.1-4.7-4.5-6.5-7.2h-.4v51.2h-22.6V137.7zm79 51.4c0-4.6-.6-9.1-1.8-13.5-1.2-4.4-3-8.2-5.4-11.6-2.4-3.4-5.4-6.1-8.9-8.1-3.6-2-7.7-3.1-12.3-3.1-9.5 0-16.7 3.3-21.5 9.9-4.8 6.6-7.2 15.4-7.2 26.4 0 5.2.6 10 1.9 14.4 1.3 4.4 3.1 8.2 5.7 11.4 2.5 3.2 5.5 5.7 9 7.5 3.5 1.9 7.6 2.8 12.2 2.8 5.2 0 9.5-1.1 13.1-3.2 3.6-2.1 6.5-4.9 8.8-8.2 2.3-3.4 4-7.2 5-11.5.9-4.3 1.4-8.7 1.4-13.2zm39.9-90.5h22.6V120h-22.6V98.6zm0 39.1h22.6v102.6h-22.6V137.7zm42.8-39.1H945v141.7h-22.6V98.6zm91.9 144.5c-8.2 0-15.5-1.4-21.9-4.1-6.4-2.7-11.8-6.5-16.3-11.2-4.4-4.8-7.8-10.5-10.1-17.1-2.3-6.6-3.5-13.9-3.5-21.8 0-7.8 1.2-15 3.5-21.6 2.3-6.6 5.7-12.3 10.1-17.1 4.4-4.8 9.9-8.5 16.3-11.2 6.4-2.7 13.7-4.1 21.9-4.1s15.5 1.4 21.9 4.1c6.4 2.7 11.8 6.5 16.3 11.2 4.4 4.8 7.8 10.5 10.1 17.1 2.3 6.6 3.5 13.8 3.5 21.6 0 7.9-1.2 15.2-3.5 21.8-2.3 6.6-5.7 12.3-10.1 17.1-4.4 4.8-9.9 8.5-16.3 11.2-6.4 2.7-13.7 4.1-21.9 4.1zm0-17.9c5 0 9.4-1.1 13.1-3.2 3.7-2.1 6.7-4.9 9.1-8.3 2.4-3.4 4.1-7.3 5.3-11.6 1.1-4.3 1.7-8.7 1.7-13.2 0-4.4-.6-8.7-1.7-13.1s-2.9-8.2-5.3-11.6c-2.4-3.4-5.4-6.1-9.1-8.2-3.7-2.1-8.1-3.2-13.1-3.2s-9.4 1.1-13.1 3.2c-3.7 2.1-6.7 4.9-9.1 8.2-2.4 3.4-4.1 7.2-5.3 11.6-1.1 4.4-1.7 8.7-1.7 13.1 0 4.5.6 8.9 1.7 13.2 1.1 4.3 2.9 8.2 5.3 11.6 2.4 3.4 5.4 6.2 9.1 8.3 3.7 2.2 8.1 3.2 13.1 3.2zm58.4-87.5h17.1v-30.8h22.6v30.8h20.4v16.9h-20.4v54.8c0 2.4.1 4.4.3 6.2.2 1.7.7 3.2 1.4 4.4.7 1.2 1.8 2.1 3.3 2.7 1.5.6 3.4.9 6 .9 1.6 0 3.2 0 4.8-.1 1.6-.1 3.2-.3 4.8-.7v17.5c-2.5.3-5 .5-7.3.8-2.4.3-4.8.4-7.3.4-6 0-10.8-.6-14.4-1.7-3.6-1.1-6.5-2.8-8.5-5-2.1-2.2-3.4-4.9-4.2-8.2-.7-3.3-1.2-7.1-1.3-11.3v-60.5h-17.1v-17.1z"
      />
    </svg>
  );
}

// ─── Language config ──────────────────────────────────────────────────────────
const flagMap: Record<string, string> = { en: 'gb', ar: 'sa', fa: 'ir' };

const languages = supportedLanguages.map((code) => ({
  code,
  name: languageNames[code],
  flag: `/images/flags/${flagMap[code] ?? code}.svg`,
  dir: ["ar", "fa"].includes(code) ? "rtl" : "ltr",
}));

const defaultLanguage = languages[0];

// ─── Default labels (SSR-safe) ────────────────────────────────────────────────
const defaultLabels = {
  about: "About",
  treatments: "Treatments",
  testimonials: "Testimonials",
  contact: "Contact",
  bookAppointment: "Book Appointment",
  lightMode: "Light Mode",
  darkMode: "Dark Mode",
  home: "Home",
};

const NAV_MENU_TRANSPARENT_VIEWPORT = "!bg-transparent !border-0 !shadow-none";

const defaultTreatmentLabels: { [key: string]: string } = {
  "treatments.dentalImplants": "Dental Implants",
  "treatments.teethWhitening": "Teeth Whitening",
  "treatments.dentalVeneers": "Dental Veneers",
  "treatments.hollywoodSmile": "Hollywood Smile",
  "treatments.allOnFour": "All-on-4 Implants",
  "treatments.rootCanal": "Root Canal Treatment",
};

// ─── Theme Toggle ─────────────────────────────────────────────────────────────
const ThemeToggle = ({
  iconClassName,
}: {
  iconClassName?: string;
}) => {
  const { theme, setTheme } = useTheme();
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  return (
    <Button
      variant="ghost"
      size="icon"
      className={cn(
        "relative h-8 w-8 rounded-full transition-colors",
        "hover:bg-white/10",
        iconClassName
      )}
      onClick={() => setTheme(theme === "light" ? "dark" : "light")}
      aria-label={
        theme === "light" ? "Switch to dark mode" : "Switch to light mode"
      }
    >
      <Sun
        className={cn(
          "h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0",
          iconClassName ?? "text-gold"
        )}
      />
      <Moon
        className={cn(
          "absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100",
          iconClassName ?? "text-slate-200"
        )}
      />
    </Button>
  );
};

// ─── Shimmer keyframes (injected once) ───────────────────────────────────────
const ShimmerStyle = () => (
  <style>{`
    @keyframes shimmer-nav {
      0%   { transform: translateX(-100%); }
      100% { transform: translateX(100%); }
    }
    .shimmer-gold-line {
      position: relative;
      overflow: hidden;
    }
    .shimmer-gold-line::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        90deg,
        transparent 0%,
        rgba(210,161,56,0.9) 40%,
        rgba(255,215,100,1) 50%,
        rgba(210,161,56,0.9) 60%,
        transparent 100%
      );
      animation: shimmer-nav 2.8s ease-in-out infinite;
    }
  `}</style>
);

// ─── Main Navbar ──────────────────────────────────────────────────────────────
export default function Navbar() {
  const { t, locale } = useTranslation("common");
  const router = useRouter();
  const { theme, setTheme } = useTheme();
  const pathname = usePathname() || "";
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredNav, setHoveredNav] = useState<string | null>(null);
  const [isTreatmentsOpen, setIsTreatmentsOpen] = useState(false);
  const treatmentsTriggerRef = useRef<HTMLButtonElement>(null);
  const { isRTL } = useRTL();
  const [mounted, setMounted] = useState(false);
  const [currentLanguage, setCurrentLanguage] = useState(defaultLanguage);
  const currentLocale = locale;

  // ── Path helpers ────────────────────────────────────────────────────────────
  const localizedPath = (path: string) => {
    if (path.startsWith(`/${currentLocale}/`)) return path;
    return `/${currentLocale}${path}`;
  };

  const isActivePath = (path: string) =>
    pathname === path ||
    pathname === localizedPath(path) ||
    pathname === `/${currentLocale}${path}`;

  // ── Context detection ───────────────────────────────────────────────────────
  const isTreatmentDetailPage = /\/treatments\/[^/]+/.test(pathname);

  // Unscrolled: always show light (white) Kinetic Glass nav on all pages
  const useLightNav = !isScrolled;

  // ── Language switching ───────────────────────────────────────────────────────
  const changeLanguage = useCallback(
    (lang: string) => {
      let newPathname = pathname;
      if (pathname.startsWith(`/${currentLocale}`)) {
        newPathname = pathname.slice(currentLocale.length + 1) || "/";
      }
      router.push(`/${lang}${newPathname}`);
      const selectedLang = languages.find((l) => l.code === lang);
      if (selectedLang) {
        document.documentElement.dir = selectedLang.dir;
        setCurrentLanguage(selectedLang);
        localStorage.setItem("preferred-locale", lang);
      }
    },
    [pathname, router, currentLocale]
  );

  // ── Effects ─────────────────────────────────────────────────────────────────
  useEffect(() => {
    setMounted(true);
    const lang =
      languages.find((l) => l.code === currentLocale) || defaultLanguage;
    setCurrentLanguage(lang);
    document.documentElement.dir = lang.dir;
  }, [currentLocale]);

  // Note: auto-redirect based on localStorage was removed because it caused
  // infinite redirect loops on 404 pages and overrode explicit URL navigation.
  // The locale is now determined solely by the URL path.

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [pathname]);

  // Lock body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isMobileMenuOpen]);

  // ── Treatments list ──────────────────────────────────────────────────────────
  const treatments = [
    {
      name: "treatments.dentalImplants",
      path: localizedPath("/treatments/dental-implants"),
    },
    {
      name: "treatments.teethWhitening",
      path: localizedPath("/treatments/teeth-whitening"),
    },
    {
      name: "treatments.dentalVeneers",
      path: localizedPath("/treatments/dental-veneers"),
    },
    {
      name: "treatments.hollywoodSmile",
      path: localizedPath("/treatments/hollywood-smile"),
    },
    {
      name: "treatments.allOnFour",
      path: localizedPath("/treatments/all-on-four"),
    },
    {
      name: "treatments.rootCanal",
      path: localizedPath("/treatments/root-canal"),
    },
  ];

  // ── Translation helpers ──────────────────────────────────────────────────────
  const getLabel = (key: string, defaultLabel: string) => {
    if (!mounted) return defaultLabel;
    const tr = t(key);
    return tr !== key ? tr : defaultLabel;
  };

  const getTreatmentLabel = (key: string) => {
    if (!mounted)
      return defaultTreatmentLabels[key] || key.split(".").pop() || "";
    const tr = t(key);
    return tr !== key ? tr : defaultTreatmentLabels[key] || key;
  };

  // ── Nav item IDs for pill tracker ────────────────────────────────────────────
  const navItems = [
    { id: "home", href: localizedPath("/"), label: getLabel("nav.home", defaultLabels.home) },
    { id: "about", href: localizedPath("/about"), label: getLabel("nav.about", defaultLabels.about) },
    { id: "treatments", href: localizedPath("/treatments"), label: getLabel("nav.treatments", defaultLabels.treatments), hasDropdown: true },
    { id: "testimonials", href: localizedPath("/testimonials"), label: getLabel("nav.testimonials", defaultLabels.testimonials) },
    { id: "contact", href: localizedPath("/contact"), label: getLabel("nav.contact", defaultLabels.contact) },
  ];

  // ── The full navbar renders server-side (no !mounted gate) so users
  //    see the real nav immediately instead of a skeleton-then-pop-in. ──

  // ────────────────────────────────────────────────────────────────────────────
  //  RENDERED NAV
  // ────────────────────────────────────────────────────────────────────────────
  return (
    <>
      <ShimmerStyle />

      {/* ── Trustpilot + stats top strip (non-sticky, only visible at top) ── */}
      {!isScrolled && (
        <div className={cn(
          "fixed top-0 left-0 right-0 z-[49] border-b border-white/10",
          isTreatmentDetailPage ? "bg-black/30" : "bg-black/20 backdrop-blur-sm"
        )}>
          <div className="flex items-center justify-center gap-3 sm:gap-5 py-1.5 px-4">
            {/* Trustpilot badge */}
            <a
              href={TRUSTPILOT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
            >
              <span className="text-xs font-bold text-white">
                {TRUSTPILOT_RATING.toFixed(1)}
              </span>
              <TrustpilotMiniStars rating={TRUSTPILOT_RATING} />
              <TrustpilotMiniLogo className="h-3.5 w-auto [&_path:last-child]:fill-white" />
            </a>

            {/* Divider */}
            <span className="hidden sm:inline w-px h-3 bg-white/20" />

            {/* Clinic stats */}
            <div className="hidden sm:flex items-center gap-4">
              <span className="text-[11px] text-white/70">
                <strong className="text-white font-semibold">10,000+</strong> Patients
              </span>
              <span className="w-px h-3 bg-white/20" />
              <span className="text-[11px] text-white/70">
                <strong className="text-white font-semibold">40+</strong> Countries
              </span>
              <span className="hidden md:inline w-px h-3 bg-white/20" />
              <span className="hidden md:inline text-[11px] text-white/70">
                <strong className="text-white font-semibold">25+</strong> Awards
              </span>
            </div>
          </div>
        </div>
      )}

      {/* ── OUTER wrapper: full-width fixed strip ─────────────────────────── */}
      <div
        className={cn(
          "fixed left-0 right-0 z-50 transition-all duration-500",
          isScrolled ? "top-0 py-2" : "top-[30px] py-0"
        )}
      >
        {/* ── SCROLLED: floating capsule ─────────────────────────────────── */}
        <AnimatePresence>
          {isScrolled && (
            <motion.div
              key="scrolled-capsule"
              initial={{ opacity: 0, y: -20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -20, scale: 0.95 }}
              transition={{ duration: 0.35, ease: [0.32, 0.72, 0, 1] }}
              className={cn(
                "mx-auto max-w-5xl px-4",
                isRTL ? "rtl" : "ltr"
              )}
            >
              <div
                className={cn(
                  "flex items-center justify-between",
                  "bg-white/80 dark:bg-navy/80",
                  "backdrop-blur-xl",
                  "border border-white/60 dark:border-white/10",
                  "rounded-full shadow-[0_4px_32px_rgba(0,0,0,0.12)]",
                  "px-4 h-14"
                )}
              >
                {/* Logo */}
                <Link
                  href={localizedPath("/")}
                  className="shrink-0 flex items-center"
                >
                  <Image
                    src="/images/logos/Logo.webp"
                    alt="DentaWista"
                    width={62}
                    height={44}
                    priority
                    style={{ width: "auto", height: "2.75rem" }}
                  />
                </Link>

                {/* Center nav */}
                <div className="hidden md:flex items-center gap-0.5">
                  {navItems.map((item) =>
                    item.hasDropdown ? (
                      <div
                        key={item.id}
                        className="relative"
                        onMouseEnter={() => setHoveredNav(item.id)}
                        onMouseLeave={() => setHoveredNav(null)}
                      >
                        {hoveredNav === item.id && (
                          <motion.div
                            layoutId="scrolled-pill"
                            className="absolute inset-0 rounded-full bg-navy/8 dark:bg-white/8"
                            transition={{ type: "spring", stiffness: 500, damping: 40 }}
                          />
                        )}
                        {/* Treatments dropdown (scrolled) */}
                        <NavigationMenu>
                          <NavigationMenuList>
                            <NavigationMenuItem>
                              <NavigationMenuTrigger
                                ref={treatmentsTriggerRef}
                                className={cn(
                                  "relative z-10 bg-transparent px-3.5 py-1.5 text-sm font-medium rounded-full",
                                  "hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[state=active]:bg-transparent",
                                  isTreatmentDetailPage
                                    ? "text-gold hover:text-gold"
                                    : "text-navy dark:text-slate-200 hover:text-navy-700 dark:hover:text-white",
                                  "transition-colors duration-150"
                                )}
                              >
                                {item.label}
                              </NavigationMenuTrigger>
                              <NavigationMenuContent>
                                <TreatmentDropdownGrid
                                  treatments={treatments}
                                  getTreatmentLabel={getTreatmentLabel}
                                  getLabel={getLabel}
                                  defaultLabels={defaultLabels}
                                  localizedPath={localizedPath}
                                  isActivePath={isActivePath}
                                  currentLocale={currentLocale}
                                  t={t}
                                  variant="scrolled"
                                />
                              </NavigationMenuContent>
                            </NavigationMenuItem>
                          </NavigationMenuList>
                        </NavigationMenu>
                      </div>
                    ) : (
                      <div
                        key={item.id}
                        className="relative"
                        onMouseEnter={() => setHoveredNav(item.id)}
                        onMouseLeave={() => setHoveredNav(null)}
                      >
                        {hoveredNav === item.id && (
                          <motion.div
                            layoutId="scrolled-pill"
                            className="absolute inset-0 rounded-full bg-navy/8 dark:bg-white/8"
                            transition={{ type: "spring", stiffness: 500, damping: 40 }}
                          />
                        )}
                        <Link
                          href={item.href}
                          className={cn(
                            "relative z-10 block px-3.5 py-1.5 text-sm font-medium rounded-full",
                            "transition-colors duration-150",
                            isActivePath(item.href.replace(`/${currentLocale}`, ""))
                              ? "text-gold"
                              : "text-navy dark:text-slate-200 hover:text-navy-700 dark:hover:text-white"
                          )}
                        >
                          {item.label}
                        </Link>
                      </div>
                    )
                  )}
                </div>

                {/* Right actions */}
                <div
                  className={cn(
                    "hidden md:flex items-center gap-2",
                    isRTL ? "flex-row-reverse" : ""
                  )}
                >
                  {/* Book Appointment – gold CTA in scrolled state */}
                  <Link href={localizedPath("/book-appointment")}>
                    <Button
                      size="sm"
                      className="bg-gold hover:bg-gold-600 text-navy font-semibold rounded-full px-4 text-sm shadow-sm transition-all duration-200 hover:shadow-md"
                    >
                      {getLabel("nav.bookAppointment", defaultLabels.bookAppointment)}
                    </Button>
                  </Link>

                  {/* Language Switcher */}
                  <LanguageSwitcher
                    mounted={mounted}
                    currentLanguage={currentLanguage}
                    currentLocale={currentLocale}
                    languages={languages}
                    changeLanguage={changeLanguage}
                    compact
                  />

                  {/* Theme toggle */}
                  <ThemeToggle iconClassName="text-navy dark:text-slate-200" />
                </div>

                {/* Mobile hamburger */}
                <Button
                  variant="ghost"
                  size="icon"
                  className="md:hidden h-8 w-8 rounded-full text-navy dark:text-slate-200"
                  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                  aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                  aria-expanded={isMobileMenuOpen}
                >
                  {isMobileMenuOpen ? (
                    <X className="h-5 w-5" />
                  ) : (
                    <Menu className="h-5 w-5" />
                  )}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── UNSCROLLED: transparent full-width bar ──────────────────────── */}
        <AnimatePresence>
          {!isScrolled && (
            <motion.div
              key="unscrolled-bar"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.3 }}
              className={cn(
                "flex items-center justify-between h-20 px-6 max-w-7xl mx-auto",
                isRTL ? "flex-row-reverse" : ""
              )}
            >
              {/* Logo */}
              <Link
                href={localizedPath("/")}
                className="shrink-0 flex items-center"
              >
                <Image
                  src="/images/logos/Logo.webp"
                  alt="DentaWista"
                  width={78}
                  height={56}
                  priority
                  style={{ width: "auto", height: "3.5rem" }}
                  className={cn(
                    "transition-all duration-300",
                    !useLightNav && "brightness-0"
                  )}
                />
              </Link>

              {/* Center pill navigation */}
              <div className="hidden md:flex items-center">
                <div
                  className={cn(
                    "flex items-center gap-0.5 px-2 py-1.5 rounded-full",
                    isTreatmentDetailPage
                      ? "bg-white/[0.08] border border-white/10"
                      : "bg-white/[0.06] backdrop-blur-md border border-white/10",
                    !useLightNav &&
                      "bg-navy/[0.06] border-navy/15"
                  )}
                >
                  {navItems.map((item) =>
                    item.hasDropdown ? (
                      <div
                        key={item.id}
                        className="relative"
                        onMouseEnter={() => setHoveredNav(item.id)}
                        onMouseLeave={() => setHoveredNav(null)}
                      >
                        {hoveredNav === item.id && (
                          <motion.div
                            layoutId="nav-pill"
                            className="absolute inset-0 rounded-full bg-white/15 dark:bg-white/10"
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 40,
                            }}
                          />
                        )}
                        {/* Treatments dropdown */}
                        <NavigationMenu viewportClassName={NAV_MENU_TRANSPARENT_VIEWPORT}>
                          <NavigationMenuList>
                            <NavigationMenuItem>
                              <NavigationMenuTrigger
                                ref={treatmentsTriggerRef}
                                className={cn(
                                  "relative z-10 bg-transparent px-4 py-1.5 text-sm font-medium rounded-full",
                                  "hover:bg-transparent focus:bg-transparent data-[state=open]:bg-transparent data-[state=active]:bg-transparent",
                                  useLightNav
                                    ? "text-white hover:text-white/90 dark:text-white dark:hover:text-white/90"
                                    : "text-navy hover:text-navy-700 dark:text-slate-200 dark:hover:text-white",
                                  isTreatmentDetailPage && "text-gold",
                                  "transition-colors duration-150"
                                )}
                              >
                                {item.label}
                              </NavigationMenuTrigger>
                              <NavigationMenuContent>
                                <TreatmentDropdownGrid
                                  treatments={treatments}
                                  getTreatmentLabel={getTreatmentLabel}
                                  getLabel={getLabel}
                                  defaultLabels={defaultLabels}
                                  localizedPath={localizedPath}
                                  isActivePath={isActivePath}
                                  currentLocale={currentLocale}
                                  t={t}
                                />
                              </NavigationMenuContent>
                            </NavigationMenuItem>
                          </NavigationMenuList>
                        </NavigationMenu>
                      </div>
                    ) : (
                      <div
                        key={item.id}
                        className="relative"
                        onMouseEnter={() => setHoveredNav(item.id)}
                        onMouseLeave={() => setHoveredNav(null)}
                      >
                        {hoveredNav === item.id && (
                          <motion.div
                            layoutId="nav-pill"
                            className="absolute inset-0 rounded-full bg-white/15 dark:bg-white/10"
                            transition={{
                              type: "spring",
                              stiffness: 500,
                              damping: 40,
                            }}
                          />
                        )}
                        <Link
                          href={item.href}
                          className={cn(
                            "relative z-10 block px-4 py-1.5 text-sm font-medium rounded-full",
                            "transition-colors duration-150",
                            isActivePath(
                              item.href.replace(`/${currentLocale}`, "")
                            )
                              ? "text-gold"
                              : useLightNav
                              ? "text-white hover:text-white/90 dark:text-white dark:hover:text-white/90"
                              : "text-navy hover:text-navy-700 dark:text-slate-200 dark:hover:text-white"
                          )}
                        >
                          {item.label}
                        </Link>
                      </div>
                    )
                  )}
                </div>
              </div>

              {/* Right actions */}
              <div
                className={cn(
                  "hidden md:flex items-center gap-2",
                  isRTL ? "flex-row-reverse" : ""
                )}
              >
                {/* Book Appointment */}
                <Link
                  href={localizedPath("/book-appointment")}
                  className={cn(
                    "inline-flex items-center justify-center rounded-full px-4 h-9 text-sm font-semibold transition-colors duration-200",
                    useLightNav
                      ? isTreatmentDetailPage
                        ? "bg-white/15 text-white hover:bg-white/25"
                        : "bg-white/10 backdrop-blur-sm text-white border border-white/25 shadow-sm hover:shadow-md hover:bg-white/20"
                      : "bg-navy text-gold hover:bg-navy/90"
                  )}
                >
                  {getLabel("nav.bookAppointment", defaultLabels.bookAppointment)}
                </Link>

                {/* Language Switcher */}
                <LanguageSwitcher
                  mounted={mounted}
                  currentLanguage={currentLanguage}
                  currentLocale={currentLocale}
                  languages={languages}
                  changeLanguage={changeLanguage}
                  useLightNav={useLightNav}
                  isTreatmentDetailPage={isTreatmentDetailPage}
                />

                {/* Theme toggle */}
                <ThemeToggle
                  iconClassName={
                    useLightNav ? "text-white" : "text-navy dark:text-slate-200"
                  }
                />
              </div>

              {/* Mobile hamburger */}
              <Button
                variant="ghost"
                size="icon"
                className={cn(
                  "md:hidden h-9 w-9 rounded-full",
                  useLightNav
                    ? "text-white hover:bg-white/10"
                    : "text-navy dark:text-slate-200 hover:bg-navy/10"
                )}
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <X className="h-5 w-5" />
                ) : (
                  <Menu className="h-5 w-5" />
                )}
              </Button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* ── Shimmer gold line ─────────────────────────────────────────────── */}
        {!isScrolled && (
          <div className="absolute bottom-0 left-0 right-0 overflow-hidden">
            <div className="shimmer-gold-line h-[1.5px] w-full bg-gold/20" />
          </div>
        )}
      </div>

      {/* ── MOBILE MENU ──────────────────────────────────────────────────────── */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, x: isRTL ? -300 : 300 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: isRTL ? -300 : 300 }}
            transition={{ duration: 0.3, ease: [0.32, 0.72, 0, 1] }}
            className="fixed inset-0 z-[60] bg-white dark:bg-[#0A1D44] overflow-y-auto"
            id="mobile-menu-panel"
          >
            {/* Close button */}
            <div className="flex items-center justify-between px-6 py-5 border-b border-border dark:border-white/10">
              <Link
                href={localizedPath("/")}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Image
                  src="/images/logos/Logo.webp"
                  alt="DentaWista"
                  width={56}
                  height={40}
                  priority
                  style={{ width: "auto", height: "2.5rem" }}
                />
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 rounded-full text-navy dark:text-slate-200"
                onClick={() => setIsMobileMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>

            <nav className="px-6 py-6 space-y-1">
              {/* Home */}
              <MobileNavLink
                href={localizedPath("/")}
                isActive={isActivePath("/")}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {getLabel("nav.home", defaultLabels.home)}
              </MobileNavLink>

              {/* About */}
              <MobileNavLink
                href={localizedPath("/about")}
                isActive={isActivePath("/about")}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {getLabel("nav.about", defaultLabels.about)}
              </MobileNavLink>

              {/* Treatments accordion */}
              <div>
                <button
                  onClick={() => setIsTreatmentsOpen((v) => !v)}
                  className={cn(
                    "w-full flex items-center justify-between px-4 py-3.5 rounded-xl text-base font-medium",
                    "transition-colors duration-150",
                    isTreatmentDetailPage
                      ? "text-gold bg-gold/5"
                      : "text-navy dark:text-slate-200 hover:bg-navy/5 dark:hover:bg-white/5"
                  )}
                >
                  <span>{getLabel("nav.treatments", defaultLabels.treatments)}</span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 text-current transition-transform duration-200",
                      isTreatmentsOpen ? "rotate-180" : ""
                    )}
                  />
                </button>

                <AnimatePresence>
                  {isTreatmentsOpen && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.25, ease: "easeInOut" }}
                      className="overflow-hidden"
                    >
                      <div className="pl-4 mt-1 space-y-0.5 pb-2">
                        {/* All treatments link */}
                        <Link
                          href={localizedPath("/treatments")}
                          onClick={() => setIsMobileMenuOpen(false)}
                          className={cn(
                            "flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium",
                            "transition-colors duration-150",
                            isActivePath("/treatments")
                              ? "text-gold"
                              : "text-navy/70 dark:text-slate-400 hover:text-navy dark:hover:text-white hover:bg-navy/5 dark:hover:bg-white/5"
                          )}
                        >
                          <Stethoscope className="h-4 w-4" />
                          {getLabel("nav.treatments", defaultLabels.treatments)}
                        </Link>
                        {treatments.map((treatment) => (
                          <Link
                            key={treatment.path}
                            href={treatment.path}
                            onClick={() => setIsMobileMenuOpen(false)}
                            className={cn(
                              "block px-4 py-2.5 rounded-lg text-sm font-medium",
                              "transition-colors duration-150",
                              pathname === treatment.path
                                ? "text-gold"
                                : "text-navy/70 dark:text-slate-400 hover:text-navy dark:hover:text-white hover:bg-navy/5 dark:hover:bg-white/5"
                            )}
                          >
                            {getTreatmentLabel(treatment.name)}
                          </Link>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Testimonials */}
              <MobileNavLink
                href={localizedPath("/testimonials")}
                isActive={isActivePath("/testimonials")}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {getLabel("nav.testimonials", defaultLabels.testimonials)}
              </MobileNavLink>

              {/* Contact */}
              <MobileNavLink
                href={localizedPath("/contact")}
                isActive={isActivePath("/contact")}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {getLabel("nav.contact", defaultLabels.contact)}
              </MobileNavLink>
            </nav>

            {/* Divider */}
            <div className="mx-6 border-t border-border dark:border-white/10" />

            {/* Language grid */}
            <div className="px-6 py-5">
              <h3 className="text-xs font-semibold uppercase tracking-widest text-muted-foreground mb-3">
                {t("nav.language") !== "nav.language" ? t("nav.language") : "Language"}
              </h3>
              <div className="grid grid-cols-3 gap-2">
                {languages.map((language) => (
                  <button
                    key={language.code}
                    onClick={() => {
                      changeLanguage(language.code);
                      setIsMobileMenuOpen(false);
                    }}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm font-medium",
                      "transition-colors duration-150 border",
                      currentLanguage.code === language.code
                        ? "border-gold text-gold bg-gold/5"
                        : "border-border dark:border-white/10 text-navy/80 dark:text-slate-300 hover:border-gold/50 hover:bg-gold/5"
                    )}
                  >
                    <Image
                      src={language.flag}
                      alt={language.name}
                      width={20}
                      height={15}
                      className="h-4 w-auto shrink-0"
                      style={{ width: "auto" }}
                    />
                    <span className="truncate text-xs">{language.name}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="mx-6 border-t border-border dark:border-white/10" />

            {/* Theme + Book appointment */}
            <div className="px-6 py-5 space-y-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                className="w-full justify-start gap-2 rounded-xl text-navy dark:text-slate-200"
              >
                <Sun className="h-4 w-4 rotate-0 scale-100 transition-all dark:-rotate-90 dark:scale-0 text-gold" />
                <Moon className="absolute h-4 w-4 rotate-90 scale-0 transition-all dark:rotate-0 dark:scale-100" />
                <span className="ml-2">
                  {theme === "dark"
                    ? getLabel("nav.lightMode", defaultLabels.lightMode)
                    : getLabel("nav.darkMode", defaultLabels.darkMode)}
                </span>
              </Button>

              <Link
                href={localizedPath("/book-appointment")}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <Button
                  size="lg"
                  className="w-full bg-navy dark:bg-gold text-gold dark:text-navy font-semibold rounded-xl hover:bg-navy/90 dark:hover:bg-gold-600"
                >
                  {getLabel("nav.bookAppointment", defaultLabels.bookAppointment)}
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

// ─── Sub-components ───────────────────────────────────────────────────────────

type MobileNavLinkProps = {
  href: string;
  isActive: boolean;
  onClick: () => void;
  children: React.ReactNode;
};

function MobileNavLink({ href, isActive, onClick, children }: MobileNavLinkProps) {
  return (
    <Link
      href={href}
      onClick={onClick}
      className={cn(
        "block px-4 py-3.5 rounded-xl text-base font-medium",
        "transition-colors duration-150",
        isActive
          ? "text-gold bg-gold/5"
          : "text-navy dark:text-slate-200 hover:bg-navy/5 dark:hover:bg-white/5"
      )}
    >
      {children}
    </Link>
  );
}

type TreatmentDropdownGridProps = {
  treatments: { name: string; path: string }[];
  getTreatmentLabel: (key: string) => string;
  getLabel: (key: string, defaultLabel: string) => string;
  defaultLabels: typeof defaultLabels;
  localizedPath: (path: string) => string;
  isActivePath: (path: string) => boolean;
  currentLocale: string;
  t: (key: string) => string;
  /** 'hero' = over hero (light text on glass), 'scrolled' = scrolled capsule (dark text on glass) */
  variant?: "hero" | "scrolled";
};

function TreatmentDropdownGrid({
  treatments,
  getTreatmentLabel,
  getLabel,
  defaultLabels,
  localizedPath,
  isActivePath,
  currentLocale,
  t,
  variant = "hero",
}: TreatmentDropdownGridProps) {
  const [hoveredItem, setHoveredItem] = useState<string | null>(null);
  const isHero = variant === "hero";

  return (
    <div className="p-3">
      {/* Kinetic Glass container — mirrors the main nav pill exactly */}
      <div
        className={cn(
          "flex flex-col gap-0.5 px-2 py-2 rounded-[22px]",
          "backdrop-blur-md border",
          "shadow-[0_8px_32px_rgba(0,0,0,0.12)]",
          isHero
            ? "bg-white/[0.06] border-white/10"
            : "bg-white/80 dark:bg-navy/80 border-white/60 dark:border-white/10"
        )}
      >
        {treatments.map((treatment) => (
          <div
            key={treatment.path}
            className="relative"
            onMouseEnter={() => setHoveredItem(treatment.path)}
            onMouseLeave={() => setHoveredItem(null)}
          >
            {/* Animated pill follower — same as main nav */}
            {hoveredItem === treatment.path && (
              <motion.div
                layoutId={`dropdown-pill-${isHero ? 'hero' : 'nav'}-${treatment.path}`}
                className={cn(
                  "absolute inset-0 rounded-full",
                  isHero
                    ? "bg-white/15 dark:bg-white/10"
                    : "bg-navy/8 dark:bg-white/8"
                )}
                transition={{ type: "spring", stiffness: 500, damping: 40 }}
              />
            )}
            <NavigationMenuLink asChild>
              <Link
                href={treatment.path}
                className={cn(
                  "relative z-10 block select-none rounded-full px-4 py-2 text-sm font-medium no-underline outline-none whitespace-nowrap",
                  "transition-colors duration-150",
                  isActivePath(treatment.path.replace(`/${currentLocale}`, ""))
                    ? "text-gold"
                    : isHero
                    ? "text-white/80"
                    : "text-navy/80 dark:text-slate-200"
                )}
              >
                {getTreatmentLabel(treatment.name)}
              </Link>
            </NavigationMenuLink>
          </div>
        ))}

        {/* Shimmer gold separator */}
        <div className="overflow-hidden mx-2 my-1">
          <div className="shimmer-gold-line h-[1px] w-full bg-gold/20 rounded-full" />
        </div>

        {/* All Treatments — gold accent */}
        <div
          className="relative"
          onMouseEnter={() => setHoveredItem("__all__")}
          onMouseLeave={() => setHoveredItem(null)}
        >
          {hoveredItem === "__all__" && (
            <motion.div
              layoutId={`dropdown-pill-${isHero ? 'hero' : 'nav'}-all`}
              className={cn(
                "absolute inset-0 rounded-full",
                isHero
                  ? "bg-white/15 dark:bg-white/10"
                  : "bg-navy/8 dark:bg-white/8"
              )}
              transition={{ type: "spring", stiffness: 500, damping: 40 }}
            />
          )}
          <NavigationMenuLink asChild>
            <Link
              href={localizedPath("/treatments")}
              className="relative z-10 flex items-center justify-between rounded-full px-4 py-2 text-sm font-medium text-gold no-underline outline-none group whitespace-nowrap"
            >
              <span>
                {t("nav.allTreatments") !== "nav.allTreatments"
                  ? t("nav.allTreatments")
                  : "All Treatments"}
              </span>
              <ArrowRight className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
            </Link>
          </NavigationMenuLink>
        </div>
      </div>
    </div>
  );
}

// ─── Language Switcher ────────────────────────────────────────────────────────
type LanguageSwitcherProps = {
  mounted: boolean;
  currentLanguage: (typeof languages)[0];
  currentLocale: string;
  languages: typeof languages;
  changeLanguage: (lang: string) => void;
  useLightNav?: boolean;
  compact?: boolean;
  isTreatmentDetailPage?: boolean;
};

function LanguageSwitcher({
  mounted,
  currentLanguage,
  currentLocale,
  languages,
  changeLanguage,
  useLightNav,
  compact,
  isTreatmentDetailPage,
}: LanguageSwitcherProps) {
  if (!mounted) {
    return (
      <div
        className={cn(
          "flex items-center gap-1 px-2.5 py-1.5 rounded-full",
          useLightNav
            ? isTreatmentDetailPage
              ? "bg-white/10"
              : "bg-white/10 border border-white/20"
            : "bg-navy/8 border border-navy/15"
        )}
      >
        <Image
          src={defaultLanguage.flag}
          alt={defaultLanguage.name}
          width={20}
          height={15}
          className="h-4 w-auto"
          style={{ width: "auto" }}
        />
      </div>
    );
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className={cn(
            "flex items-center gap-1.5 px-2.5 py-1.5 rounded-full h-auto",
            "transition-colors duration-150",
            "focus-visible:ring-1 focus-visible:ring-gold focus-visible:ring-offset-1 ring-offset-transparent",
            useLightNav
              ? isTreatmentDetailPage
                ? "text-white/80 hover:text-white hover:bg-white/10"
                : "text-white/80 hover:text-white hover:bg-white/10 border border-white/20"
              : "text-navy/70 dark:text-slate-300 hover:text-navy dark:hover:text-white hover:bg-navy/8 dark:hover:bg-white/8 border border-navy/15 dark:border-white/15"
          )}
        >
          <Image
            src={currentLanguage.flag}
            alt={currentLanguage.name}
            width={20}
            height={15}
            className="h-4 w-auto"
            style={{ width: "auto" }}
          />
          {!compact && (
            <ChevronDown className="h-3 w-3 opacity-60" />
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="center"
        sideOffset={8}
        className="w-[170px] rounded-xl shadow-xl border border-border/50 dark:border-white/10 bg-white dark:bg-navy p-1.5"
      >
        {languages.map((lang) => (
          <DropdownMenuItem
            key={lang.code}
            onClick={() => changeLanguage(lang.code)}
            className={cn(
              "flex items-center gap-2.5 cursor-pointer text-sm py-2 px-3 rounded-lg",
              "transition-colors duration-100",
              currentLocale === lang.code
                ? "bg-gold/10 text-gold font-medium"
                : "text-navy dark:text-slate-200 hover:bg-navy/5 dark:hover:bg-white/8"
            )}
          >
            <Image
              src={lang.flag}
              alt={lang.name}
              width={20}
              height={15}
              className="h-4 w-auto shrink-0"
              style={{ width: "auto" }}
            />
            <span>{lang.name}</span>
            {currentLocale === lang.code && (
              <span className="ml-auto text-gold text-xs">✓</span>
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
